import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from "recharts";
import { CHART_COLORS, SENSOR_NAMES, SENSOR_UNITS } from "../utils/constants";

/**
 * SensorChart component renders real-time line charts for sensor data.
 */
export function SensorChart({
  title,
  data,
  dataKeys,
  colors = Object.values(CHART_COLORS),
  yDomain,
  referenceLines = [],
  height = 200,
}) {
  if (!data || data.length === 0) {
    return (
      <div className="chart-container">
        <h3>{title}</h3>
        <div className="chart-placeholder">
          <p>Waiting for data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chart-container">
      <h3>{title}</h3>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart
          data={data}
          margin={{ top: 5, right: 20, left: 10, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis
            dataKey="cycle"
            stroke="#9ca3af"
            fontSize={10}
            label={{ value: "Cycle", position: "bottom", fill: "#6b7280", fontSize: 11, offset: 0 }}
          />
          <YAxis
            stroke="#9ca3af"
            fontSize={10}
            domain={yDomain || ["auto", "auto"]}
            tickFormatter={(value) =>
              typeof value === "number" ? value.toFixed(2) : value
            }
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "#1f2937",
              border: "1px solid #374151",
              borderRadius: "8px",
            }}
            labelFormatter={(value) => `Cycle ${value}`}
            formatter={(value, name) => {
              const unit = getUnitForKey(name);
              const formattedValue = typeof value === "number" ? value.toFixed(2) : value;
              return [
                unit ? `${formattedValue} ${unit}` : formattedValue,
                formatDataKey(name),
              ];
            }}
          />
          <Legend
            formatter={(value) => formatDataKey(value)}
            verticalAlign="bottom"
            align="center"
            wrapperStyle={{
              fontSize: "11px",
              paddingTop: "15px",
              paddingLeft: "50px",
            }}
          />

          {referenceLines.map((line, idx) => (
            <ReferenceLine
              key={idx}
              y={line.value}
              stroke={line.color}
              strokeDasharray="5 5"
              label={{
                value: line.label,
                fill: line.color,
                fontSize: 10,
              }}
            />
          ))}

          {dataKeys.map((key, index) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={colors[index % colors.length]}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * FeatureChart specifically for displaying slope/std features.
 * Dynamically shows the sensors passed in (can change over time based on importance).
 */
export function FeatureChart({ data, sensors, featureType = "slope", title }) {
  const defaultTitle = featureType === "slope" ? "Trend (Slope)" : "Variance (Std)";

  if (!data || data.length === 0) {
    return (
      <div className="chart-container">
        <h3>{title || defaultTitle}</h3>
        <div className="chart-placeholder">
          <p>Waiting for features...</p>
        </div>
      </div>
    );
  }

  const dataKeys = sensors.map(
    (s) => `S${s.replace("sensor_", "")}_${featureType}`
  );

  const colors =
    featureType === "slope"
      ? ["#ef4444", "#f97316", "#eab308", "#22c55e"]
      : ["#3b82f6", "#8b5cf6", "#ec4899", "#06b6d4"];

  return (
    <SensorChart
      title={title || defaultTitle}
      data={data}
      dataKeys={dataKeys}
      colors={colors}
      referenceLines={
        featureType === "slope"
          ? [
              { value: 0, color: "#6b7280", label: "No trend" },
              { value: -0.05, color: "#ef4444", label: "Degradation" },
            ]
          : []
      }
    />
  );
}

/**
 * RiskChart shows risk score over time.
 */
export function RiskChart({ data }) {
  if (!data || data.length === 0) {
    return (
      <div className="chart-container">
        <h3>Risk Score Over Time</h3>
        <div className="chart-placeholder">
          <p>Waiting for risk predictions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chart-container">
      <h3>Risk Score Over Time</h3>
      <ResponsiveContainer width="100%" height={175}>
        <LineChart
          data={data}
          margin={{ top: 5, right: 20, left: 10, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis
            dataKey="cycle"
            stroke="#9ca3af"
            fontSize={10}
            label={{ value: "Cycle", position: "bottom", fill: "#6b7280", fontSize: 11, offset: 0 }}
          />
          <YAxis
            stroke="#9ca3af"
            fontSize={10}
            domain={[0, 1]}
            tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
            label={{ value: "Risk %", angle: -90, position: "insideLeft", fill: "#6b7280", fontSize: 11 }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "#1f2937",
              border: "1px solid #374151",
              borderRadius: "8px",
            }}
            labelFormatter={(value) => `Cycle ${value}`}
            formatter={(value) => [`${(value * 100).toFixed(1)}%`, "Risk"]}
          />

          {/* Threshold lines */}
          <ReferenceLine
            y={0.7}
            stroke="#ef4444"
            strokeDasharray="5 5"
            label={{ value: "Alert", fill: "#ef4444", fontSize: 10 }}
          />
          <ReferenceLine
            y={0.5}
            stroke="#f59e0b"
            strokeDasharray="5 5"
            label={{ value: "Warning", fill: "#f59e0b", fontSize: 10 }}
          />

          <Line
            type="monotone"
            dataKey="risk_score"
            stroke="#ef4444"
            strokeWidth={3}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function formatDataKey(key) {
  // Convert sensor_2 to "S2 (LPC Outlet Temp)" or S2_mean to "S2 Mean"
  const sensorMatch = key.match(/^(sensor_|S)(\d+)(_(\w+))?$/);
  if (sensorMatch) {
    const sensorNum = sensorMatch[2];
    const sensorKey = `sensor_${sensorNum}`;
    const suffix = sensorMatch[4] ? ` ${sensorMatch[4].charAt(0).toUpperCase() + sensorMatch[4].slice(1)}` : "";
    const name = SENSOR_NAMES[sensorKey];
    return name ? `S${sensorNum}${suffix}` : `S${sensorNum}${suffix}`;
  }
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (l) => l.toUpperCase());
}

function getUnitForKey(key) {
  // Extract sensor number and return unit
  const sensorMatch = key.match(/^(sensor_|S)(\d+)/);
  if (sensorMatch) {
    const sensorKey = `sensor_${sensorMatch[2]}`;
    return SENSOR_UNITS[sensorKey] || "";
  }
  return "";
}
